import Phaser from "phaser";
import { MUSIC, MUSIC_VOLUMES, type MusicId, type MusicVolume, renderTrack } from "./music";

/** Rendered at this rate; Web Audio resamples it. Plenty for these soft voices. */
const SAMPLE_RATE = 22050;

/**
 * Plays the chosen background music on a loop through Phaser's sound manager, so the menu's
 * Ljud switch mutes it too and it pauses with the page. Each track is rendered the first time
 * it is chosen. Without Web Audio it stays silent.
 */
export class MusicPlayer {
	private sound: Phaser.Sound.BaseSound | null = null;
	private playing: MusicId | null = null;
	private level = 0;

	constructor(private readonly scene: Phaser.Scene) {}

	/** Starts, changes or (null) stops the music; the same track only changes its volume. */
	set(id: MusicId | null, volume: MusicVolume): void {
		const level = MUSIC_VOLUMES[volume];
		this.level = level;
		if (id === this.playing && this.sound) {
			(this.sound as Phaser.Sound.WebAudioSound).setVolume(level);
			return;
		}
		this.stop();
		if (!id) return;
		const manager = this.scene.sound;
		if (!(manager instanceof Phaser.Sound.WebAudioSoundManager)) return;
		const key = `music-${id}`;
		if (!this.scene.cache.audio.exists(key)) {
			const samples = renderTrack(MUSIC[id], SAMPLE_RATE);
			const buffer = manager.context.createBuffer(1, samples.length, SAMPLE_RATE);
			buffer.copyToChannel(samples, 0);
			this.scene.cache.audio.add(key, buffer);
		}
		this.sound = manager.add(key, { loop: true, volume: level });
		this.sound.play();
		this.playing = id;
	}

	/** What is playing, for `gameDebug.audio`. */
	get current(): { id: MusicId; volume: number; isPlaying: boolean } | null {
		if (!this.playing || !this.sound) return null;
		const sound = this.sound as Phaser.Sound.WebAudioSound;
		return { id: this.playing, volume: this.level, isPlaying: sound.isPlaying };
	}

	stop(): void {
		this.sound?.destroy();
		this.sound = null;
		this.playing = null;
	}
}
